import React, { useEffect, useState } from 'react';
import { View, Modal, FlatList } from 'react-native';
import { translate } from '../../../services/translate';
import FormInput from '../../../components/FormInput';
import FormActions from '../../../components/FormActions';
import { WindowInformation } from '../../../services/window-information';
import { createStyleSheet, useStyles } from 'react-native-unistyles';
import { useForm } from 'react-hook-form';
import { useIsFocused } from '@react-navigation/native';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../../../context/Auth';
import { accessService } from '../../../services/central-api/access';
import MyAppText from '../../../components/MyAppText';
import moment from 'moment';
import ActingBodiesDropdown from '../../../components/access/ActingBodiesDropdown';
import Toast from 'react-native-toast-message';
import { ClientError } from '../../../services/central-api/base-service';
import MyDatePicker from '../../../components/MyDatePicker';
import FormDropDownPicker from '../../../components/FormDropDownPicker';

interface ModalProps {
    isModalVisible: boolean;
    setModalVisible: React.Dispatch<React.SetStateAction<boolean>>;
    actingBodyUnitId?: number;
    onClose: () => void;
}

interface ActingBodyUnitForm {
    actingBodyId: number;
    name: string;
    effectiveDate: Date | null;
    reissueAllReports: 'yes' | 'no';
}

export default function ActingBodyUnitModal({ actingBodyUnitId, isModalVisible, setModalVisible, onClose }: ModalProps) {
    const { styles } = useStyles(styleSheet);
    const windowInfo = WindowInformation();
    const { user: authenticatedUser } = useAuth();

    const [step, setStep] = useState<number>(1);

    const isFocused = useIsFocused();
    const queryResult = useQuery({
        queryKey: ['getActingBodyUnitModal', actingBodyUnitId, isModalVisible],
        queryFn: () => accessService.getActingBodyUnitById(actingBodyUnitId!),
        enabled: !!authenticatedUser && !!actingBodyUnitId && isModalVisible && isFocused,
        staleTime: 0,
    });

    const { data } = queryResult;

    const defaultEmptyValues: ActingBodyUnitForm = {
        actingBodyId: 0,
        name: '',
        effectiveDate: null,
        reissueAllReports: 'no'
    };

    const form = useForm<ActingBodyUnitForm>({
        defaultValues: defaultEmptyValues,
        mode: 'onChange'
    });
    const { isValid, dirtyFields } = form.formState;
    const nameChanged = !!dirtyFields.name;
    const historyData = React.useMemo(() => data?.nomenclatureHistory ?? [], [data]);

    useEffect(() => {
        if (data) {
            form.reset(data);
            setStep(1);
        }
    }, [data]);

    function clearValues() {
        setModalVisible(false);
        form.reset(defaultEmptyValues);
        setStep(1);
        onClose();
    }

    async function updateActingBodyUnit() {
        try {
            const data = form.getValues();
            if (actingBodyUnitId && data.effectiveDate) {
                await accessService.updateActingBodyUnit({
                    name: data.name,
                    effectiveDate: data.effectiveDate,
                    reissueAllReports: data.reissueAllReports == 'yes',
                    id: actingBodyUnitId
                });

                Toast.show({
                    type: 'sentinelxSuccess',
                    text1: translate('ActionSuccessfully'),
                });

                clearValues();
            }
        } catch (err) {
            if (err instanceof ClientError) {
                return Toast.show({
                    type: 'sentinelxError',
                    text1: translate(err.message),
                });
            }

            console.error(err);
            Toast.show({
                type: 'sentinelxError',
                text1: translate('unexpectedError'),
            });
        }
    }

    async function createActingBodyUnit() {
        try {
            const data = form.getValues();
            await accessService.createActingBodyUnit(data);

            Toast.show({
                type: 'sentinelxSuccess',
                text1: translate('ActionSuccessfully'),
            });

            clearValues();
        } catch (err) {
            if (err instanceof ClientError) {
                return Toast.show({
                    type: 'sentinelxError',
                    text1: translate(err.message),
                });
            }

            console.error(err);
            Toast.show({
                type: 'sentinelxError',
                text1: translate('unexpectedError'),
            });
        }
    }

    function renderItem({ item }: { item: { id: number; name: string; effectiveDate: string; }; }) {
        const history = data?.nomenclatureHistory ?? [];

        if (history.length === 0) {
            return <MyAppText style={styles.stepText}>{translate('noNameChange')}</MyAppText>;
        }

        const currentIndex = history.findIndex(h => h.id === item.id);
        if (currentIndex === -1) {
            return null;
        }

        let dateRangeContent = '';

        if (currentIndex == 0) {
            dateRangeContent = `${moment(item.effectiveDate).format('DD/MM/YYYY')} - ${translate('current')}`;
        } else {
            const previous = history[currentIndex - 1];
            dateRangeContent = `${moment(item.effectiveDate).format('DD/MM/YYYY')} - ${moment(previous.effectiveDate).subtract(1, 'day').format('DD/MM/YYYY')}`;
        }

        return (
            <View style={{ justifyContent: 'space-between', flexDirection: 'row' }}>
                <MyAppText style={styles.historyContent}>{item.name}</MyAppText>
                <MyAppText style={styles.historyContent}>{dateRangeContent}</MyAppText>
            </View>
        );
    }

    if (!actingBodyUnitId) {
        return (
            <Modal transparent={true}
                visible={isModalVisible}
                animationType='fade'
                onRequestClose={() => clearValues()}
            >
                <View style={styles.container}>
                    <View style={styles.formContainer(windowInfo.isMobile, 400, 300)}>
                        <View style={{ gap: 24 }}>
                            <View style={{ flexDirection: 'column', gap: 8 }}>
                                <MyAppText style={styles.headerText}>{translate('createActingBodyUnit')}</MyAppText>
                            </View>
                            <View style={{ gap: 24 }}>
                                <ActingBodiesDropdown
                                    includeAllOption={false}
                                    form={form}
                                    fieldName='actingBodyId'
                                    zIndex={2}
                                    required={true}
                                    labelFontSize={14}
                                />
                                <FormInput
                                    label={translate('actingBodyUnitName')}
                                    placeholder={translate('actingBodyUnitName')}
                                    form={form}
                                    fieldName='name'
                                    required={true}
                                    labelFontSize={14}
                                />
                            </View>
                        </View>
                        <FormActions
                            onSubmit={createActingBodyUnit}
                            onClose={() => clearValues()}
                            disabled={!form.formState.isValid}
                        />
                    </View>
                </View>
            </Modal>
        );
    }

    return (
        <Modal transparent={true}
            visible={isModalVisible}
            animationType='fade'
            onRequestClose={() => clearValues()}
        >
            <View style={styles.container}>
                <View style={styles.formContainer(windowInfo.isMobile, 580, step == 1 ? 500 : 325)}>
                    <View style={{ gap: 24, flex: 1 }}>
                        <View style={{ flexDirection: 'column', gap: 8 }}>
                            <MyAppText style={styles.headerText}>{translate('editActingBodyUnitInformation')}</MyAppText>
                            <MyAppText style={styles.stepText}>{translate('stepXofY', { x: step, y: 2 })}</MyAppText>
                        </View>
                        {step == 1 ? <>
                            <View style={{ rowGap: 24, zIndex: 3 }}>
                                <ActingBodiesDropdown
                                    includeAllOption={false}
                                    form={form}
                                    fieldName='actingBodyId'
                                    zIndex={3}
                                    required={true}
                                    disabled={!!actingBodyUnitId}
                                    labelFontSize={14}
                                />
                                <View style={{ flexDirection: 'row', gap: 24, zIndex: 2 }}>
                                    <View style={{ flex: 1 }}>
                                        <FormInput
                                            label={translate('actingBodyUnitName')}
                                            placeholder={translate('actingBodyUnitName')}
                                            form={form}
                                            fieldName='name'
                                            required={true}
                                            labelFontSize={14}
                                        />
                                    </View>
                                    <MyDatePicker
                                        form={form}
                                        dateFieldName='effectiveDate'
                                        required
                                        label={translate('changeDate')}
                                        maxDate={moment().subtract(1, 'day').toDate()}
                                        minDate={moment(data?.createdAt).add(1, 'day').toDate()}
                                        labelFontSize={14}
                                        zIndex={2}
                                        viewStyle={{ maxWidth: 150, maxHeight: 40, rowGap: 5 }}
                                    />
                                </View>
                                <MyAppText style={styles.historyContent}>{translate('changeDateDetails')}</MyAppText>
                            </View>
                            <View style={{ flexDirection: 'column', gap: 8 }}>
                                <MyAppText style={styles.stepText}>{translate('previousNames')}</MyAppText>
                                <FlatList
                                    scrollEnabled={true}
                                    style={{ maxHeight: 95 }}
                                    data={historyData}
                                    renderItem={renderItem}
                                    contentContainerStyle={{ gap: 8 }}
                                />
                            </View>
                        </> : <View style={{ rowGap: 24, zIndex: 2 }}>
                            <View style={{ flexDirection: 'column', gap: 8 }}>
                                <MyAppText style={styles.historyContent}>{translate('updateOccurrenceReportExplanation')}</MyAppText>
                                <MyAppText style={styles.historyContent}>{translate('updateOccurrenceReportQuestion')}</MyAppText>
                            </View>
                            <FormDropDownPicker
                                label={translate('reissueAllReports')}
                                items={['yes', 'no'].map(type => ({ label: translate(type), value: type }))}
                                multiple={false}
                                fieldName='reissueAllReports'
                                form={form}
                                zIndex={2}
                                required={true}
                                viewStyle={{ maxWidth: 239 }}
                            />
                        </View>
                        }
                    </View>
                    <FormActions
                        confirmText={step == 1 ? 'forward' : 'save'}
                        onSubmit={() => {
                            if (step == 1) {
                                setStep(2);
                            } else {
                                updateActingBodyUnit();
                            }
                        }}
                        onClose={() => clearValues()}
                        disabled={!isValid || !nameChanged}
                    />
                </View>
            </View>
        </Modal>
    );
}

const styleSheet = createStyleSheet((theme) => ({
    container: {
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        backgroundColor: '#31313199',
        flex: 1
    },
    formContainer: (isMobile: boolean, width: number, minHeight: number) => {
        return {
            width: isMobile ? '95%' : width,
            minHeight: minHeight,
            backgroundColor: theme.colors.container.background,
            borderRadius: 8,
            padding: 32,
            rowGap: 24
        };
    },
    headerText: {
        fontSize: 18,
        fontWeight: '700',
        color: theme.colors.neutral['500']
    },
    stepText: {
        color: theme.colors.primary['500'],
        fontWeight: '600',
        fontSize: 16,
    },
    historyContent: {
        fontSize: 14,
        color: theme.colors.neutral['400'],
        fontWeight: '600',
    }
}));
